import { useToast } from "@chakra-ui/react";
import { FC, useCallback } from "react";
import { graphql } from "react-relay";

import { DistributionStatusSwitch_ActivateMutation as ActivateMutation } from "~/src/__relay_artifacts__/DistributionStatusSwitch_ActivateMutation.graphql";
import { DistributionStatusSwitch_DeactivateMutation as DeactivateMutation } from "~/src/__relay_artifacts__/DistributionStatusSwitch_DeactivateMutation.graphql";
import { Switch, SwitchProps } from "~/src/components/common/Switch";
import { useMutationCommit } from "~/src/lib/react-relay";

type Props = {
  id: string;
  name: string;
  isChecked: boolean;
} & Pick<SwitchProps, "isDisabled" | "isReadOnly">;

const formatDisabledLinkedModuleMessage = (
  modules: ReadonlyArray<{ readonly name: string }>
): string => {
  const names = modules.map((m) => m.name).join("、");
  return `関連するJavaScriptタグ「${names}」は無効のままです。設定 → タグ から有効化し、公開してください。`;
};

const activateMutation = graphql`
  mutation DistributionStatusSwitch_ActivateMutation(
    $input: ActivateDistributionStatusableInput!
  ) {
    updateStatus: activateDistributionStatusable(input: $input) {
      distributionStatusable {
        status
      }
      disabledLinkedUserTagModules {
        id
        name
        effectiveEnabled
      }
    }
  }
`;

const deactivateMutation = graphql`
  mutation DistributionStatusSwitch_DeactivateMutation(
    $input: DeactivateDistributionStatusableInput!
  ) {
    updateStatus: deactivateDistributionStatusable(input: $input) {
      distributionStatusable {
        status
      }
    }
  }
`;

export const DistributionStatusSwitch: FC<
  React.PropsWithChildren<React.PropsWithChildren<Props>>
> = ({ id, name, isChecked, ...props }) => {
  const toast = useToast();

  const activate = useMutationCommit<ActivateMutation>(activateMutation);
  const deactivate = useMutationCommit<DeactivateMutation>(deactivateMutation);

  const handleStatusChange = useCallback(async () => {
    try {
      const variables = { input: { distributionStatusableId: id } };

      if (isChecked) {
        const res = await deactivate({ variables });
        const statusable = res.updateStatus?.distributionStatusable;
        if (!statusable) throw new Error("assertion failed");

        toast({
          title: `${name}のステータスを更新しました`,
          status: "success",
        });
        return;
      }

      const res = await activate({ variables });
      const updateStatus = res.updateStatus;
      const statusable = updateStatus?.distributionStatusable;
      if (!statusable) throw new Error("assertion failed");

      toast({
        title: `${name}のステータスを更新しました`,
        status: "success",
      });

      const disabledModules = updateStatus?.disabledLinkedUserTagModules ?? [];
      if (disabledModules.length > 0) {
        toast({
          title: formatDisabledLinkedModuleMessage(disabledModules),
          status: "warning",
          duration: 8000,
          isClosable: true,
        });
      }
    } catch (err) {
      toast({
        title: `${name}のステータスの更新に失敗しました`,
        status: "error",
      });
    }
  }, [activate, deactivate, id, isChecked, name, toast]);

  return (
    <Switch isChecked={isChecked} onChange={handleStatusChange} {...props} />
  );
};
